import { useCallback, useEffect, useState, memo, useRef } from "react";
import { useSelector } from "react-redux";
import MonthVolumeAnalysis from "./MonthVolume";
import { getStateOutagePowerType, StateOutageSectorResponse } from "../../../../../../../Rest_api/RestApi";

function MonthAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  selectedTargetDate,
  selectedState,
  selectedExchanges = [],
  selectedMarkets = [],
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  selectedTargetDate: any[];
  selectedState?: string;
  selectedExchanges?: string[];
  selectedMarkets?: string[];
}) {
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StateOutageSectorResponse | null>(null);
  const [availableDates, setAvailableDates] = useState<any[]>([]);
  const [filterDates, setFilterDates] = useState<string[]>([]);
  const [selectedFilterDate, setSelectedFilterDate] = useState<string>("");

  // Sync available dates from Redux
  useEffect(() => {
    const dateKey = "Power Market";
    const trendDates = DefaultDates?.[dateKey]?.defaultTrendDates || [];
    setAvailableDates(trendDates);
  }, [DefaultDates]);

  const handleFetchData = useCallback(async () => {
    if (availableDates.length < 2 || !selectedState) {
      setData(null);
      return;
    }

    try {
      setLoading(true);
      const range1 = availableDates[0].value.split(",");
      const range2 = availableDates[1].value.split(",");

      const startDate1 = range1[0];
      const endDate1 = range1[1] || startDate1;

      const startDate2 = range2[0];
      const endDate2 = range2[1] || startDate2;

      const response = await getStateOutagePowerType(
        startDate1,
        endDate1,
        startDate2,
        endDate2,
        selectedState,
        selectedExchanges,
        selectedMarkets
      );
      setData(response);

      // Extract all unique dates from response
      const dates = new Set<string>();
      if (response.range_1) Object.keys(response.range_1).forEach(d => dates.add(d));
      if (response.range_2) Object.keys(response.range_2).forEach(d => dates.add(d));

      const sortedDates = Array.from(dates).sort();
      setFilterDates(sortedDates);

      // Default to the last available date if current selection is invalid
      if (sortedDates.length > 0) {
        setSelectedFilterDate(prev => sortedDates.includes(prev) ? prev : sortedDates[0]);
      } else {
        setSelectedFilterDate("");
      }

    } catch (error) {
      console.error("Error fetching state outage power type data", error);
      setError?.("Failed to fetch state outage power type data");
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [
    // eslint-disable-next-line
    JSON.stringify(availableDates),
    selectedState,
    // eslint-disable-next-line
    JSON.stringify(selectedExchanges),
    // eslint-disable-next-line
    JSON.stringify(selectedMarkets),
    setError,
  ]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  return (
    <>
      <div className="dash-card border border-[#718EBF1C] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] w-full !min-h-[100%] rounded-2xl">
        <div className="flex justify-between items-center mb-0 px-4 py-2 border-b border-gray-100">
          <h2 className="chartHeading !text-sm">Outage Analysis by Type</h2>
          <div className="flex items-center gap-3">
            {filterDates.length > 0 && (
              <CustomDropdown
                options={filterDates}
                selected={selectedFilterDate}
                onSelect={setSelectedFilterDate}
              />
            )}
          </div>
        </div>
        <MonthVolumeAnalysis
          selectedBlock={selectedBlock}
          refreshKey={refreshKey}
          setError={setError}
          loading={loading}
          selectedTargetDate={selectedTargetDate}
          data={data}
          selectedDate={selectedFilterDate}
        />
      </div>
    </>
  );
}

const CustomDropdown = ({ options, selected, onSelect }: { options: string[], selected: string, onSelect: (val: string) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="appearance-none bg-white border border-gray-300 text-gray-700 py-0 h-6 pl-2 pr-5 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500 text-xs font-medium flex items-center whitespace-nowrap min-w-[140px] justify-between"
      >
        <span>{selected ? formatDate(selected) : "Select Date"}</span>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 max-h-40 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg z-50 w-full">
          {options.map((date) => (
            <div
              key={date}
              className={`px-3 py-1 text-xs hover:bg-gray-100 cursor-pointer text-gray-700 ${selected === date ? "bg-blue-50 font-medium text-blue-700" : ""}`}
              onClick={() => {
                onSelect(date);
                setIsOpen(false);
              }}
            >
              {formatDate(date)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(MonthAnalysis);
